import { Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { IWardMeasurementType } from "../../../@types/admin/AdminMeasurementType";
import { TableSorted } from "../../../components/TableSorted";
import { useWardMeasurementTypes } from "../../../hooks/queries/admin/measurementTypes";
import { useAllWardOptions } from "../../../hooks/queries/admin/selectValues";
import { useModal } from "../../../hooks/useModal";
import {
  AdminSelectField,
  BooleanCell,
  ErrorMessage,
  FilterButton,
  FilterGroup,
  FilterLabel,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
  WarningNotice,
} from "../shared";
import { RowActions } from "./AdminWards.styles";
import { ApplyMeasurementTypeTemplateModal } from "./ApplyMeasurementTypeTemplateModal";
import { RemoveWardMeasurementTypeModal } from "./RemoveWardMeasurementTypeModal";
import { WardMeasurementTypeModal } from "./WardMeasurementTypeModal";

const SEARCH_FIELDS: (keyof IWardMeasurementType)[] = [
  "measurementTypeCode",
  "measurementTypeDisplayName",
  "categoryDisplayName",
];

/**
 * Ward × Measurement Types admin page (EN-5157), migrated from the Retool
 * WardMeasurementTypes page plus `moduleWardMeasurementTypes`. A ward
 * selector drives the ward's configured measurement types; each can be
 * added, edited or removed, and a template can be applied to the whole ward.
 *
 * Two facts drive the extra warnings here, both from EN-5060 §4.3.1:
 * a ward's configuration existing is a switch, not just display config —
 * writing the first row cuts the ward over from condition-derived measurement
 * types, and removing the last row reverts it. Retool exposed both as
 * unremarkable row actions.
 *
 * Scope note: the ticket anticipated a threshold editor. There isn't one
 * here, because `UpdateWardMeasurementTypeRequestDto` is displayInPortal /
 * displayInApp / order, and the min/max values belong to the catalogue entry,
 * which is read-only reference data seeded out of band. The one per-ward
 * threshold that does exist — the 1MSTS pulse-ox cutoff (EN-5194/EN-5195) —
 * is edited on the ward's details page (`WardConfigurationForm`).
 */
export function AdminWardMeasurementTypes() {
  document.title = "Ward measurement types - Admin";

  const { openModal, closeModal } = useModal();
  const { wards, isLoading: wardsLoading } = useAllWardOptions();
  const [selectedWardId, setSelectedWardId] = useState<number | undefined>();

  const { wardMeasurementTypes, isLoading, isError, isLoaded } =
    useWardMeasurementTypes(selectedWardId);

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: wardMeasurementTypes,
    searchFields: SEARCH_FIELDS,
  });

  const selectedWard = wards.find((ward) => ward.id === selectedWardId);
  const wardName = selectedWard
    ? `${selectedWard.id} - ${selectedWard.name}`
    : "";
  const isWardUnconfigured = isLoaded && wardMeasurementTypes.length === 0;

  const openTypeModal = (measurementType?: IWardMeasurementType) => {
    if (selectedWardId === undefined) return;
    openModal(
      <WardMeasurementTypeModal
        wardId={selectedWardId}
        wardName={wardName}
        measurementType={measurementType}
        isWardUnconfigured={isWardUnconfigured}
        onClose={closeModal}
      />,
      { width: "480px", showCloseButton: true }
    );
  };

  const openRemoveModal = (measurementType: IWardMeasurementType) => {
    if (selectedWardId === undefined) return;
    openModal(
      <RemoveWardMeasurementTypeModal
        wardId={selectedWardId}
        wardName={wardName}
        measurementType={measurementType}
        isLastType={wardMeasurementTypes.length === 1}
        onClose={closeModal}
      />,
      { width: "480px", showCloseButton: true }
    );
  };

  const openTemplateModal = () => {
    if (selectedWardId === undefined) return;
    openModal(
      <ApplyMeasurementTypeTemplateModal
        wardId={selectedWardId}
        wardName={wardName}
        currentTypes={wardMeasurementTypes}
        onClose={closeModal}
      />,
      { width: "min(820px, 94vw)", maxHeight: "90vh", showCloseButton: true }
    );
  };

  const columns: ColumnDef<IWardMeasurementType, string>[] = useMemo(
    () => [
      {
        accessorKey: "measurementTypeDisplayName",
        header: "Measurement type name",
        enableSorting: true,
      },
      {
        accessorKey: "measurementTypeCode",
        header: "Type code",
        enableSorting: true,
      },
      {
        accessorKey: "categoryDisplayName",
        header: "Category",
        enableSorting: true,
      },
      {
        // TableSorted dashes a falsy cell, so a `false` flag would read as
        // "no data" rather than "off" — the accessor renders the word.
        id: "displayInPortal",
        header: "Display in portal",
        accessorFn: (type) => (type.displayInPortal ? "Yes" : "No"),
        cell: ({ row }) => (
          <BooleanCell value={row.original.displayInPortal}>
            {row.original.displayInPortal ? "Yes" : "No"}
          </BooleanCell>
        ),
        enableSorting: true,
      },
      {
        id: "displayInApp",
        header: "Display in app",
        accessorFn: (type) => (type.displayInApp ? "Yes" : "No"),
        cell: ({ row }) => (
          <BooleanCell value={row.original.displayInApp}>
            {row.original.displayInApp ? "Yes" : "No"}
          </BooleanCell>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Edit"
              icon={<Pencil1Icon />}
              onClick={() => {
                openTypeModal(row.original);
              }}
            />
            <RowAction
              label="Remove"
              icon={<TrashIcon />}
              danger
              onClick={() => {
                openRemoveModal(row.original);
              }}
            />
          </RowActions>
        ),
      },
    ],
    [selectedWardId, wardName, wardMeasurementTypes, isWardUnconfigured]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Ward measurement types</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="measurement-types-ward">Ward</FilterLabel>
          <AdminSelectField
            id="measurement-types-ward"
            value={selectedWardId ?? ""}
            disabled={wardsLoading}
            onChange={(e) =>
              setSelectedWardId(
                e.target.value ? Number(e.target.value) : undefined
              )
            }
          >
            <option value="">
              {wardsLoading ? "Loading..." : "Select a ward"}
            </option>
            {wards.map((ward) => (
              <option key={ward.id} value={ward.id}>
                {ward.id} - {ward.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        {selectedWardId !== undefined && (
          <SearchInput
            type="text"
            placeholder="Search by measurement type, code, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}

        <FiltersActions>
          <FilterButton
            variant="secondary"
            disabled={selectedWardId === undefined}
            onClick={openTemplateModal}
          >
            Apply template
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={selectedWardId === undefined}
            onClick={() => openTypeModal()}
          >
            Add measurement type
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load this ward&apos;s measurement types. Try refreshing the
          page.
        </ErrorMessage>
      )}

      {isWardUnconfigured && (
        <WarningNotice>
          {wardName} has no measurement types configured, so its patients take
          theirs from their conditions. Adding the first one switches the ward
          over to this list.
        </WarningNotice>
      )}

      {selectedWardId === undefined ? (
        <TableContainer>
          <TableWrapper refreshKeys={[["admin-ward-measurement-types"]]}>
            <TableSorted
              columns={columns}
              data={[]}
              emptyStateText="Select a ward to see its measurement types"
            />
          </TableWrapper>
        </TableContainer>
      ) : (
        <TableContainer>
          <TableWrapper refreshKeys={[["admin-ward-measurement-types"]]}>
            <TableSorted
              columns={columns}
              data={items}
              emptyStateText={
                isLoading ? <TableLoadingState /> : "No rows found"
              }
            />
          </TableWrapper>
          <TablePagination
            exportCsv={{
              filename: "ward-measurement-types",
              columns,
              rows: allItems,
            }}
            pagination={pagination}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </TableContainer>
      )}
    </PageContainer>
  );
}
